import React, { useEffect, useRef, useState } from "react";
import { trackEvent, trackNonInteractionEvent } from "@leafly-com/web-utils";
import cx from "classnames";
import Link from "next/link";

import { useAddToBagContext } from "context/AddToBagContext";
import { Dispensary } from "custom-types/Dispensary";
import { MenuItem, Weights } from "custom-types/MenuItem";
import { getAvailableItems } from "hooks/useGetAvailableMenuItems";
import { useSelector } from "redux/hooks";
import { getCoordinates, getCountryCode } from "redux/selectors/location";
import { getStatusMessage } from "utils/dispensaryScheduleUtils";
import { getDistance } from "utils/distance";
import { getCents, getDollars } from "utils/formatPrice";
import { getMenuTypeLabel } from "utils/menuTypeUtils";

import BigPrice from "components/BigPrice";
import Dropdown from "components/botanic/Dropdown";
import DropdownItem from "components/botanic/Dropdown/DropdownItem";
import StarRating from "components/botanic/StarRating";
import Image from "components/Image";

import { ComparisonShoppingTablePlaceholder } from "./ComparisonShoppingTablePlaceholder";

type Props = {
  defaultSort?: string;
  initialWeight: string;
  menuItems: MenuItem[];
  menuItemsLoading: boolean;
  menuItemWeights: Weights;
  onAddToBagClick?: () => void;
  strainName: string;
  trackingCategory: string;
};

const ComparisonShoppingTable = ({
  defaultSort = "distance",
  initialWeight,
  menuItems,
  menuItemsLoading,
  menuItemWeights,
  onAddToBagClick = () => null,
  strainName,
  trackingCategory,
}: Props) => {
  const {
    selectedVariantId,
    setSelectedVariantId,
    setButtonRef,
    addingToBag,
    setAddingToBag,
  } = useAddToBagContext();

  const returnFocusRef = useRef(null);

  const [loading, setLoading] = useState(menuItemsLoading);
  const [items, setItems] = useState(menuItems);
  const [sortByPrice, setSortByPrice] = useState(defaultSort === "price");

  const [selectedWeight, setSelectedWeight] = useState(initialWeight);

  const countryCode = useSelector(getCountryCode);
  const ucCountryCode = countryCode?.toUpperCase();

  const coordinates = useSelector(getCoordinates);

  const onSortSelection = (newSortByPrice: boolean) => {
    setLoading(true);

    getAvailableItems(
      strainName,
      selectedWeight,
      newSortByPrice ? "price" : undefined,
      coordinates,
      countryCode,
    ).then((data) => {
      if (data?.menuItems?.length) {
        setItems(data.menuItems);
      }
      setSortByPrice(newSortByPrice);
      setLoading(false);
    });

    trackEvent(
      trackingCategory,
      "Click",
      newSortByPrice ? "Price" : "Distance",
    );
  };

  const onClickSetSelectedWeight = (weight: string) => {
    setLoading(true);
    getAvailableItems(
      strainName,
      weight,
      defaultSort,
      coordinates,
      countryCode,
    ).then((data) => {
      if (data?.menuItems?.length) {
        setItems(data.menuItems);
        setSelectedWeight(weight);
      }

      setLoading(false);
    });
    trackEvent(trackingCategory, "Click", "Flower Sizes");
  };

  const onClickDispensaryName = (item: MenuItem) => {
    const dispensaryTypeText = getMenuTypeLabel({
      isCA: countryCode === "CA",
      tags: item.tags as Dispensary["tags"],
    });
    trackEvent(trackingCategory, "Click", dispensaryTypeText, {
      brandId: item?.brand?.id,
      dispensaryId: item?.dispensaryId,
      milesAway: item?.distanceMi?.toFixed(1),
    });
  };

  const onClickSetSelectedVariantId = (itemId: number) => {
    setSelectedVariantId(itemId);
    setButtonRef(returnFocusRef);
    setAddingToBag(true);
    const menuItem = items.find((x) => x.id === itemId);
    const featuredDeal = menuItem?.deal || menuItem?.offers?.[0];
    trackEvent(trackingCategory, "Click", "Add to Bag Button", {
      brandId: menuItem?.brand?.id,
      dealDiscountType: featuredDeal?.discountType || "no deal",
      dealId: featuredDeal?.id || "no deal",
      dealType: featuredDeal?.kind || "no deal",
      dispensaryId: menuItem?.dispensaryId,
      milesAway: menuItem?.distanceMi?.toFixed(1),
    });
  };

  const getFinalDistance = (dispensary: Dispensary) => {
    // if delivery ONLY, we do not show distance
    if (dispensary?.deliveryEnabled && !dispensary?.hasReservationsEnabled) {
      return null;
    }

    return `${getDistance(dispensary?.distanceMi, ucCountryCode)
      ?.replace("mi", " miles")
      .replace("km", " kilometers")}  away`;
  };

  useEffect(() => {
    if (!items?.length && menuItems?.length) {
      trackNonInteractionEvent(
        trackingCategory,
        "Impression",
        "Add to Bag Button",
      );
    }
  }, [menuItems]);

  return loading ? (
    <ComparisonShoppingTablePlaceholder />
  ) : items?.length && menuItemWeights?.length ? (
    <div>
      <div className="md:flex justify-between">
        <ul className="flex mb-lg -mx-xs">
          {menuItemWeights.map((weight, i) => (
            <li
              className="mx-xs"
              data-testid="comparison-shopping-table-item"
              id={weight.value}
              key={`${weight.value}-${i}`}
            >
              <button
                disabled={addingToBag}
                className={cx(
                  `rounded border py-xs px-sm text-xs font-bold leading-normal
                   transition hover:bg-green-20 hover:border-green-60`,
                  {
                    "bg-green-20 border-green-60":
                      selectedWeight === weight.value,
                    "bg-white border-light-grey":
                      selectedWeight !== weight.value,
                  },
                )}
                aria-label={`select ${weight.value}`}
                onClick={() => onClickSetSelectedWeight(weight.value)}
                data-testid="comp-shop-weight"
              >
                {weight.value}
              </button>
            </li>
          ))}
        </ul>
        <div className="flex items-center ml-xs md:ml-none mb-md md:mb-none">
          <span className="text-xs mr-xs">Sort by</span>
          <Dropdown
            className="text-xs items-center"
            aria-label="Sort by"
            selectedItem={sortByPrice ? "Price" : "Distance"}
          >
            <DropdownItem onClick={() => onSortSelection(false)}>
              Distance
            </DropdownItem>
            <DropdownItem onClick={() => onSortSelection(true)}>
              Price
            </DropdownItem>
          </Dropdown>
        </div>
      </div>
      <ul ref={returnFocusRef}>
        {items.map((item, i) => {
          const { dispensary, deal } = item;
          const menuTypeLabel = getMenuTypeLabel({
            isCA: countryCode === "CA",
            tags: dispensary.tags as Dispensary["tags"],
          });
          const formattedDistance = getFinalDistance(dispensary);
          const { currentStatuses } = item.dispensary;
          const currentStatus = getStatusMessage(
            dispensary.preorderConfigured,
            currentStatuses?.store,
            item.dispensary.timeZone,
            "store",
          );

          return (
            <li key={item.id} className={i !== items.length - 1 ? "mb-lg" : ""}>
              <div className="disp-card rounded bg-white border-2 border-light-grey flex items-stretch text-left w-full">
                <div className="p-lg border-r border-light-grey flex-grow">
                  <>
                    <div className="flex justify-between">
                      <div style={{ height: 32, minHeight: 32, width: 32 }}>
                        {dispensary?.logoUrl && (
                          <Image
                            className="rounded"
                            src={dispensary?.logoUrl}
                            sizes={[32]}
                            alt={`${dispensary?.name} logo`}
                          />
                        )}
                      </div>
                      {!!menuTypeLabel && (
                        <div
                          className="text-xs font-bold bg-white border border-light-grey rounded px-xs"
                          data-testid="comp-shop-dispensary-indicator"
                          style={{ height: "20px" }}
                        >
                          {menuTypeLabel}
                        </div>
                      )}
                    </div>
                    <h2 className="text-sm font-bold underline">
                      <Link
                        href={`/dispensary-info/${
                          dispensary?.slug
                        }?strain_name[]=${encodeURIComponent(
                          strainName,
                        )}#products`}
                        onClick={() => onClickDispensaryName(item)}
                      >
                        {dispensary?.name}
                      </Link>
                    </h2>
                    <StarRating
                      rating={dispensary?.roundedRating}
                      ratingCount={dispensary?.reviewCount}
                    />

                    {currentStatus && (
                      <div className="flex items-center">
                        {
                          <span
                            className={cx("rounded-full mr-sm", {
                              "bg-green": currentStatuses?.store?.isOpen,
                              "border border-green":
                                !currentStatuses?.store?.isOpen,
                            })}
                            style={{ height: 8, width: 8 }}
                          ></span>
                        }
                        <span className="text-xs" data-testid="statusMessage">
                          {currentStatus}
                        </span>
                      </div>
                    )}
                    <span className="text-grey text-xs">
                      {formattedDistance}
                    </span>
                  </>
                </div>
                <div className="basis-[125px] p-lg flex-grow-0 flex flex-col justify-between">
                  <div>
                    <span
                      className={cx(
                        "text-lg block mb-xs",
                        deal?.active ? "text-notification" : "",
                      )}
                    >
                      <BigPrice
                        dollars={getDollars(
                          deal?.active
                            ? deal.discountedPrice / 100
                            : item.price,
                        )}
                        cents={getCents(
                          deal?.active
                            ? deal.discountedPrice / 100
                            : item.price,
                        )}
                        symbol="$"
                        size="large"
                      />
                    </span>
                    {deal?.active && deal?.discountedPrice && item.price && (
                      <span className="text-xs block mb-xs text-grey font-bold">
                        reg{" "}
                        {`${getDollars(item.price)}.${getCents(item.price)}`}
                      </span>
                    )}
                    {deal?.active && deal?.discountAmount && (
                      <span className="text-xs inline-block py-xs px-sm rounded bg-notification mb-xs text-white font-bold">
                        {deal.discountLabel}
                      </span>
                    )}
                    {!deal?.active && (
                      <span className="text-xs block">
                        {item.normalizedQuantity}
                      </span>
                    )}
                  </div>
                  <button
                    className="button self-end mt-md mx-auto flex items-center justify-center rounded-full whitespace-nowrap"
                    onClick={() => {
                      onAddToBagClick();
                      onClickSetSelectedVariantId(item.id);
                    }}
                    aria-label="Add selected item to cart"
                    data-testid="comp-shop-atb"
                    disabled={addingToBag}
                  >
                    {addingToBag && selectedVariantId === item.id
                      ? // Maybe someday we can revisit this, but animations just destroy
                        // the modal in safari
                        // <>
                        //   <SpinningGanj className="mr-xs" />
                        //   <span className="leading-none">adding</span>
                        // </>
                        "...adding"
                      : "add to cart"}
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  ) : (
    <></>
  );
};

export default ComparisonShoppingTable;
